/**
 * scheduleListConverters.js
 *
 * DashboardScheduleListPage から抽出された純粋関数群
 * スケジュール一覧画面固有のデータ変換・タイムライン生成ロジックを提供
 */

/**
 * 値を Date オブジェクトに変換
 * @param {string|Date|null} value - 日付値
 * @returns {Date|null} Date オブジェクト、無効な場合は null
 */
export function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 日付の時刻部分を 00:00:00 にリセット
 * @param {Date} date - 日付オブジェクト
 * @returns {Date} 時刻がリセットされた日付
 */
export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * 日付に指定日数を加算
 * @param {Date} date - 基準日
 * @param {number} amount - 加算する日数（負の値で減算）
 * @returns {Date} 加算後の日付
 */
export function addDays(date, amount) {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
}

/**
 * 2つの日付間の日数差を計算
 * @param {Date} from - 開始日
 * @param {Date} to - 終了日
 * @returns {number} 日数差
 */
export function diffInDays(from, to) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((to.getTime() - from.getTime()) / msPerDay);
}

/**
 * 値を指定範囲内に制限
 * @param {number} value - 値
 * @param {number} min - 最小値
 * @param {number} max - 最大値
 * @returns {number} 制限された値
 */
export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * タイムライン情報を構築
 * @param {Array<Object>} rows - スケジュール行配列（start, end フィールドを含む）
 * @returns {Object} {startDay, days, monthSegments, yearSegments, dayCount}
 */
export function buildTimeline(rows) {
  const dates = rows.flatMap((item) => [item.start, item.end]).filter(Boolean);

  const now = new Date();
  const fallbackStart = startOfDay(now);
  const minDate = dates.length ? new Date(Math.min(...dates.map((d) => d.getTime()))) : fallbackStart;
  const maxDate = dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : addDays(fallbackStart, 365);

  const start = startOfDay(minDate);
  const end = startOfDay(maxDate);
  const dayCount = Math.max(diffInDays(start, end) + 1, 1);

  // 日単位の配列を作成
  const days = Array.from({ length: dayCount }, (_, index) => {
    const date = addDays(start, index);
    return {
      key: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      label: `${date.getMonth() + 1}/${date.getDate()}`,
      date,
    };
  });

  // 月ごとのセグメントを作成（ヘッダー表示用）
  const monthSegments = [];
  days.forEach((item) => {
    const current = monthSegments[monthSegments.length - 1];
    if (current && current.year === item.year && current.month === item.month) {
      current.count += 1;
    } else {
      monthSegments.push({ year: item.year, month: item.month, count: 1 });
    }
  });

  // 年ごとのセグメントを作成
  const yearSegments = [];
  days.forEach((item) => {
    const current = yearSegments[yearSegments.length - 1];
    if (current && current.year === item.year) {
      current.count += 1;
    } else {
      yearSegments.push({ year: item.year, count: 1 });
    }
  });

  return { startDay: start, days, monthSegments, yearSegments, dayCount };
}

/**
 * ステータスから表示色トーンを判定
 * @param {string} status - ステータス名
 * @returns {string} トーン名（'info'|'done'|'delay'|'progress'）
 */
export function statusToTone(status) {
  if (!status) return 'info';
  if (status === '完了') return 'done';
  if (status === '遅延') return 'delay';
  if (status === '進行中') return 'progress';
  return 'info';
}

/**
 * タイムラインバーのスタイルを計算
 * @param {Object} item - スケジュールアイテム（start, end を含む）
 * @param {Date} timelineStart - タイムラインの開始日
 * @param {number} daysLength - タイムラインの総日数
 * @param {number} dayWidth - 1日あたりの幅（px）
 * @returns {Object} {left, width} または {display: 'none'} スタイルオブジェクト
 */
export function buildBarStyle(item, timelineStart, daysLength, dayWidth) {
  if (!item.start) return { display: 'none' };
  const startOffset = diffInDays(timelineStart, startOfDay(item.start));
  const endOffset = diffInDays(timelineStart, startOfDay(item.end || item.start));

  const visibleStart = clamp(startOffset, 0, daysLength - 1);
  const visibleEnd = clamp(endOffset, visibleStart, daysLength - 1);

  return {
    left: `${visibleStart * dayWidth + 0.5}px`,
    width: `${(visibleEnd - visibleStart + 1) * dayWidth - 1}px`,
  };
}
